// frontend/src/pages/Register.jsx
import React, { useState, useEffect, useRef ,useCallback} from 'react'; // useRef اضافه شد
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaCalendarAlt } from 'react-icons/fa'; // FaCalendarAlt اضافه شد
import JalaliDateInput from '../components/JalaliDateInput'; // مسیر درست

export default function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    national_id: '',
    phone_number: '',
    referral_code: '',
    password: '',
    birth_date: '',
  });
  const [code, setCode] = useState(['', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const codeInputRef = useRef([]); // useRef اینجا استفاده شده
  const navigate = useNavigate();
  const dateInputRef = useRef(null);
  /* ---------- 1. اعتبارسنجی کد ملی ---------- */
  const validateNationalId = (id) => {
    id = convertToLatinDigits(id);
    if (!/^\d{10}$/.test(id)) return false;
    const check = +id[9];
    const sum = id.slice(0, 9).split('').reduce((acc, digit, i) => acc + +digit * (10 - i), 0);
    const remainder = sum % 11;
    return remainder < 2 ? check === remainder : check === 11 - remainder;
  };
  const handleBirthDateChange = useCallback((value) => {
    const formatted = value.formatted || '';
    setFormData(prev => ({ ...prev, birth_date: formatted }));
  }, []); // وابستگی خالی → همیشه یکسان
  /* ---------- 2. تبدیل اعداد فارسی/عربی به انگلیسی ---------- */
  const convertToLatinDigits = (input) => {
    if (!input) return input;
    const persianDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    let result = input;
    for (let i = 0; i < 10; i++) {
      result = result.replace(persianDigits[i], i).replace(arabicDigits[i], i);
    }
    return result;
  };

  /* ---------- 3. ارزیابی قدرت رمز عبور ---------- */
  const evaluatePasswordStrength = (password) => {
    let strength = '';
    let valid = true;

    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasUpper || !hasLower || !hasDigit || !hasMinLength) valid = false;

    if (password.length < 6) strength = 'ضعیف';
    else if (password.length < 8 || !(hasUpper && hasLower && hasDigit)) strength = 'متوسط';
    else strength = 'قوی';

    setPasswordStrength(strength);
    setPasswordValid(valid);
    return valid;
  };

  /* ---------- 4. پر کردن خودکار کد پیامک ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sms = params.get('sms');
    if (sms && sms.length === 5) {
      const digits = sms.split('').slice(0, 5);
      setCode(digits);
      if (step === 2) autoVerify(digits.join(''));
    }

    const stored = localStorage.getItem('pendingSMS');
    if (stored && stored.length === 5) {
      const digits = stored.split('').slice(0, 5);
      setCode(digits);
      localStorage.removeItem('pendingSMS');
      if (step === 2) autoVerify(digits.join(''));
    }
  }, [step]);

  const autoVerify = async (verificationCode) => {
    try {
      const res = await api.post('/api/auth/verify', {
        phone_number: formData.phone_number,
        code: verificationCode,
      });
      localStorage.setItem('authToken', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(res.data.user));
      setSuccess(`ثبت‌نام موفق، خوش آمدید ${res.data.user.name || res.data.user.first_name}`);
      setTimeout(() => navigate('/login_page'), 1500);
    } catch (err) {
      setError('کد خودکار نامعتبر بود');
      setTimeout(() => setError(''), 3000);
    }
  };

  /* ---------- 5. تایمر ارسال مجدد ---------- */
  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  /* ---------- 6. کد معرف از URL ---------- */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) setFormData(prev => ({ ...prev, referral_code: ref }));
  }, []);

  /* ---------- 7. هندلرهای ورودی ---------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'national_id' || name === 'phone_number') {
      processedValue = convertToLatinDigits(value);
    }

    if (name === 'password') evaluatePasswordStrength(processedValue);

    setFormData({ ...formData, [name]: processedValue });
  };

  const handleCodeChange = (e, index) => {
    const value = e.target.value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 4) codeInputRef.current[index + 1]?.focus();
    else if (!value && index > 0) codeInputRef.current[index - 1]?.focus();
  };

  /* ---------- 8. اعتبارسنجی تاریخ تولد ---------- */
  const validateBirthDate = (value) => {
    if (!value) return false;
    const latinValue = convertToLatinDigits(value);
    const regex = /^\d{4}\/(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])$/;
    return regex.test(latinValue);
  };

  const formatDateForBackend = (date) => {
    if (!date) return date;
    const latinDate = convertToLatinDigits(date);
    const parts = latinDate.split('/');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    }
    return latinDate;
  };

  /* ---------- 9. ثبت‌نام ---------- */
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateNationalId(formData.national_id)) {
      setError('کد ملی نامعتبر است');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (!formData.birth_date || !validateBirthDate(formData.birth_date)) {
      setError('فرمت تاریخ تولد نامعتبر است (مثال: 1370/01/01)');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (!passwordValid) {
      setError('رمز عبور باید شامل حروف کوچک، بزرگ، عدد و حداقل ۸ کاراکتر باشد');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/register', {
        ...formData,
        birth_date: formatDateForBackend(formData.birth_date),
      });
      setStep(2);
      setTimer(60);
      setCanResend(false);
      setFormData(prev => ({ ...prev, phone_number: res.data.phone_number }));
      setSuccess(res.data.message || 'کد تأیید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ثبت‌نام');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- 10. ارسال مجدد کد ---------- */
  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    try {
      await api.post('/api/auth/login/request-code', { phone_number: formData.phone_number });
      setCode(['', '', '', '', '']);
      setTimer(60);
      setCanResend(false);
      setSuccess('کد جدید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال مجدد');
      setTimeout(() => setError(''), 3000);
    }
  };

  /* ---------- 11. تأیید کد ---------- */
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const verificationCode = code.join('');
    if (verificationCode.length !== 5) {
      setError('کد ۵ رقمی را کامل وارد کنید');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      const res = await api.post('/api/auth/verify', {
        phone_number: formData.phone_number,
        code: verificationCode,
      });
      localStorage.setItem('authToken', res.data.accessToken);
      localStorage.setItem('refreshToken', res.data.refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(res.data.user));
      setSuccess(`ثبت‌نام موفق، خوش آمدید ${res.data.user.name || res.data.user.first_name}`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'کد اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  /* ---------- 12. Toast ---------- */
  const Toast = ({ type, message }) => (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 ${
        type === 'error' ? 'bg-red-500/90 text-white border-red-300' : 'bg-green-500/90 text-white border-green-300'
      } backdrop-blur-lg`}
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        {type === 'error' ? (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
        ) : (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
        )}
      </svg>
      <span className="font-medium">{message}</span>
    </motion.div>
  );

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-8 font-vazir">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 100 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/30 relative"
        >

          {/* اسپینر مرکزی */}
          {loading && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-3xl">
              <FaSpinner className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
          )}

          <motion.h2
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-3xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8"
          >
            {step === 1 ? 'ثبت‌نام' : 'تأیید کد'}
          </motion.h2>

          {/* مرحله ۱ */}
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleRegister}
                className="space-y-5"
              >
                {/* نام و نام خانوادگی */}
                <div className="grid grid-cols-2 gap-3">
                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نام</label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} required className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                  </motion.div>
                  <motion.div whileFocus={{ scale: 1.02 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} required className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                  </motion.div>
                </div>

                {/* کد ملی */}
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">کد ملی</label>
                  <input type="text" name="national_id" value={formData.national_id} onChange={handleInputChange} required maxLength={10} placeholder="۰۰۱۲۳۴۵۶۷۸" className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                </motion.div>

                {/* شماره تلفن */}
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">شماره تلفن</label>
                  <input type="text" name="phone_number" value={formData.phone_number} onChange={handleInputChange} required placeholder="۰۹۱۲۳۴۵۶۷۸۹" className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                </motion.div>

                {/* تاریخ تولد - با JalaliDateInput */}
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ تولد</label>
                  <div className="flex items-center gap-2">
                  <JalaliDateInput
  ref={dateInputRef}
  onChange={handleBirthDateChange}
  className="flex-1 px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50 text-center font-mono text-lg"
/>
  <div
    className="p-2 bg-indigo-100 rounded-xl text-indigo-600 cursor-pointer"

    onClick={() => {
      
      // فوکوس به input
      const input = document.querySelector('input[placeholder="1___/__/__"]');
      if (input) input.focus();
    }}
  >
    <FaCalendarAlt className="w-5 h-5" />
  </div>
</div>
                </motion.div>

                {/* کد معرف */}
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">کد معرف (اختیاری)</label>
                  <input type="text" name="referral_code" value={formData.referral_code} onChange={handleInputChange} placeholder="ABC123" className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                </motion.div>

                {/* رمز عبور + قدرت */}
                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">رمز عبور</label>
                  <input type="password" name="password" value={formData.password} onChange={handleInputChange} required className="w-full px-3 py-2 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 bg-gray-50" />
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs font-medium ${
                      passwordStrength === 'ضعیف' ? 'text-red-600' :
                      passwordStrength === 'متوسط' ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {passwordStrength && `${passwordStrength} `}
                    </span>
                    {formData.password && (passwordValid ? <FaCheckCircle className="text-green-600" /> : <FaTimesCircle className="text-red-600" />)}
                  </div>
                </motion.div>

                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={loading} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-70">
                  {loading ? 'در حال بررسی...' : 'ثبت‌نام و ارسال کد'}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* مرحله ۲ */}
          <AnimatePresence mode="wait">
            {step === 2 && (
              <motion.form
                key="verify"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleVerify}
                className="space-y-6"
              >
                <p className="text-center text-sm text-gray-600">
                  کد ۵ رقمی به <span className="font-mono text-indigo-700">{formData.phone_number}</span> ارسال شد
                </p>

                <div className="flex justify-center gap-3" dir="ltr">
                  {code.map((digit, index) => (
                    <motion.input
                      key={index}
                      ref={(el) => (codeInputRef.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleCodeChange(e, index)}
                      className="w-14 h-14 text-center text-2xl font-bold border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                      maxLength={1}
                      required
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                    />
                  ))}
                </div>

                <div className="text-center">
                  {!canResend ? (
                    <p className="text-sm text-gray-500">{timer} ثانیه تا ارسال مجدد</p>
                  ) : (
                    <motion.button type="button" whileHover={{ scale: 1.05 }} onClick={handleResendCode} className="text-indigo-600 font-medium underline">
                      ارسال مجدد کد
                    </motion.button>
                  )}
                </div>

                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all">
                  تأیید کد
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* ورود */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center mt-8">
            <p className="text-sm text-gray-600 mb-2">قبلاً حساب دارید؟</p>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/login_page')} className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all">
              ورود به حساب
            </motion.button>
          </motion.div>
        </motion.div>

        {/* پیام‌ها */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <AnimatePresence>
            {error && <Toast type="error" message={error} />}
            {success && <Toast type="success" message={success} />}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}